import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { debugLog } from '../utils/debug';
interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  isPolling: boolean;
  joinMission: (missionId: string) => void;
  leaveMission: (missionId: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const { token, user } = useAuth();

  // Polling disabled — Socket.IO handles real-time updates.
  // The /api/missions/updates endpoint is not implemented.
  // Re-enable if a REST fallback is added: useMissionPolling(!connected && !!token);


  useEffect(() => {
    if (token && user) {
      // Initialize socket connection
      // Empty string or undefined means same-origin (behind nginx proxy)
      // The string 'disabled' explicitly disables WebSocket
      const socketUrl = import.meta.env.VITE_SOCKET_URL || '';

      // Skip WebSocket only if explicitly disabled
      if (socketUrl === 'disabled') {
        debugLog('WebSocket explicitly disabled, using polling mode');
        setIsPolling(true);
        return;
      }

      // Empty string = same origin, which is correct for nginx-proxied setup
      const newSocket = io(socketUrl, {
        auth: {
          token,
        },
        transports: ['websocket', 'polling'],
        path: '/socket.io/',
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
      });

      newSocket.on('connect', () => {
        debugLog('Socket connected:', newSocket.id);
        setConnected(true);
        setIsPolling(false);
      });

      newSocket.on('disconnect', () => {
        debugLog('Socket disconnected');
        setConnected(false);
        setIsPolling(true);
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error.message);
        setIsPolling(true);
      });

      newSocket.on('mission:updated', (data) => {
        // Handle mission updates
        window.dispatchEvent(new CustomEvent('mission:updated', { detail: data }));
      });

      newSocket.on('mission:created', (data) => {
        // Handle new missions
        window.dispatchEvent(new CustomEvent('mission:created', { detail: data }));
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Clean up socket when logged out
      if (socket) {
        socket.close();
        setSocket(null);
      }
      setConnected(false);
      setIsPolling(false);
    }
  }, [token, user]);

  const joinMission = (missionId: string) => {
    if (socket && connected) {
      socket.emit('mission:join', missionId);
    }
  };

  const leaveMission = (missionId: string) => {
    if (socket && connected) {
      socket.emit('mission:leave', missionId);
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        connected,
        isPolling,
        joinMission,
        leaveMission,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};